/**
 * HIBP breach check cu k-anonymity (Have I Been Pwned Pwned Passwords API v3).
 *
 * Flow:
 *   1. SHA1(password) local via WebCrypto
 *   2. Trimitem la `api.pwnedpasswords.com/range/{first5hex}` doar prefix
 *   3. Serverul HIBP întoarce lista de suffix-uri pt toate hash-urile care
 *      încep cu acele 5 chars, plus count breaches per suffix
 *   4. Căutăm local match pe ultimii 35 chars → returnăm count sau 0
 *
 * Parola nu părăsește niciodată browserul. HIBP vede doar primii 5 hex
 * din SHA1, insuficient pt a identifica parola (k-anonymity, ~500 hash-uri
 * împart un prefix în medie).
 */

export interface HibpResult {
  /** De câte ori apare în breach-uri cunoscute. 0 = nu apare. */
  count: number;
  /** Flag convenience: count > 0. */
  pwned: boolean;
}

const API_BASE = 'https://api.pwnedpasswords.com/range/';

export async function checkPasswordPwned(password: string): Promise<HibpResult> {
  if (!password) return { count: 0, pwned: false };

  const sha1Hex = await sha1Hex_(password);
  const prefix = sha1Hex.slice(0, 5).toUpperCase();
  const suffix = sha1Hex.slice(5).toUpperCase();

  const resp = await fetch(`${API_BASE}${prefix}`, {
    method: 'GET',
    headers: { 'Add-Padding': 'true' }, // HIBP: adaugă padding ca să nu se poată inferi count-ul din lungime
    credentials: 'omit',
    mode: 'cors',
  });
  if (!resp.ok) {
    throw new Error(`HIBP: status ${resp.status}`);
  }
  const text = await resp.text();
  for (const line of text.split(/\r?\n/)) {
    const colonIdx = line.indexOf(':');
    if (colonIdx < 0) continue;
    const hashSuffix = line.slice(0, colonIdx).trim();
    if (hashSuffix === suffix) {
      const count = parseInt(line.slice(colonIdx + 1).trim(), 10);
      if (Number.isFinite(count) && count > 0) {
        return { count, pwned: true };
      }
    }
  }
  return { count: 0, pwned: false };
}

async function sha1Hex_(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-1', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
